import { Injectable, NgZone } from '@angular/core';


/**
 * ESC/POS "open cash drawer" pulse.
 *   ESC p m t1 t2  ->  0x1B 0x70 <pin> <onTime> <offTime>
 * Pin 2 is the standard connector and the only one we send.
 */
export const KICK_PIN2 = new Uint8Array([0x1b, 0x70, 0x00, 0x19, 0xfa]);

/** The drawer's GATT identifiers. */
export const DRAWER_SERVICE = 'e7810a71-73ae-499d-8c15-faa9aef0c3f2';
export const DRAWER_WRITE_CHAR = 'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f';

/**
 * Talks to the BLE cash drawer over Web Bluetooth. The handles are typed loosely
 * because the Web Bluetooth typings are not installed in this project.
 */
@Injectable({
  providedIn: 'root'
})
export class DrawerService {
  connected: boolean = false;
  busy: boolean = false;
  deviceName: string | null = null;

  /** When on, the drawer is kept connected after use for instant repeat kicks. */
  keepWarm: boolean = true;

  private device: any = null;
  private kickChar: any = null;
  private reconnecting: boolean = false;
  private warmUntil: number = 0;
  private readonly WARM_MS = 90000; // stay warm 90s after each kick

  constructor(private ngZone: NgZone) { }

  //The project compiles to ES2022, so async/await is native and zone.js cannot follow it.
  //Everything after an await on a Web Bluetooth promise therefore runs outside Angular's
  //zone, and writing to these fields there would never repaint the connection indicator.
  //(The drawer kicker app this came from used signals, which notify Angular regardless of
  //zone - swapping them for plain fields is what lost the updates.)
  private setState(changes: { connected?: boolean, busy?: boolean, deviceName?: string }): void {
    this.ngZone.run(() => {
      if (changes.connected !== undefined) {
        this.connected = changes.connected;
      }
      if (changes.busy !== undefined) {
        this.busy = changes.busy;
      }
      if (changes.deviceName !== undefined) {
        this.deviceName = changes.deviceName;
      }
    });
  }

  //Chrome raises NotFoundError both when the chooser is dismissed and when nothing in
  //range matched the filter, so the two cases share one message. Neither is a fault.
  private wasChooserDismissed(err: any): boolean {
    return err?.name === 'NotFoundError';
  }

  private reportConnectFailure(err: any): void {
    if (this.wasChooserDismissed(err)) {
      this.report('warn', 'No Drawer Selected', 'No drawer was picked. Check it is switched on and not already paired in Windows Bluetooth settings, then press Connect again.');
      return;
    }

    this.report('error', 'Drawer Not Connected', (err as Error)?.message ?? 'Could not reach the cash drawer.');
  }

  //Drawer messages are rendered by the till as a plain div rather than going through
  //PrimeNG's toast. The toast container is a fixed, full width overlay we do not control
  //the lifetime of, and it kept being left behind over the page; this is ours, it is
  //removed by *ngIf, and it is click-through by default.
  showNotice: boolean = false;
  noticeSeverity: string = 'info';
  noticeSummary: string = '';
  noticeDetail: string = '';
  private noticeTimer: any = null;

  showMessage(severity: string, summary: string, detail: string): void {
    this.ngZone.run(() => {
      this.noticeSeverity = severity;
      this.noticeSummary = summary;
      this.noticeDetail = detail;
      this.showNotice = true;

      if (this.noticeTimer) {
        clearTimeout(this.noticeTimer);
      }

      //Errors stay put so nobody misses them; the rest clear themselves.
      if (severity != 'error') {
        this.noticeTimer = setTimeout(() => this.ngZone.run(() => this.showNotice = false), 8000);
      }
    });
  }

  dismissNotice(): void {
    if (this.noticeTimer) {
      clearTimeout(this.noticeTimer);
      this.noticeTimer = null;
    }

    this.showNotice = false;
  }

  private report(severity: string, summary: string, detail: string): void {
    this.showMessage(severity, summary, detail);
  }

  get supported(): boolean {
    return typeof navigator !== 'undefined' && !!(navigator as any).bluetooth;
  }

  /** True once a drawer has been chosen, so we know a kick won't need the chooser. */
  get paired(): boolean {
    return this.device != null;
  }

  /**
   * The one entry point. Ensures a live connection (fast path if already warm),
   * then fires the kick. Retries once on a stale handle.
   *
   * Must be called from a user gesture the first time, because picking the device
   * opens the browser's chooser.
   */
  /** Resolves true only when the drawer actually took the pulse. */
  async openDrawer(): Promise<boolean> {
    if (!this.supported) {
      this.report('error', 'Drawer Unavailable', 'This browser cannot talk to the cash drawer. Use Chrome or Edge over HTTPS.');
      return false;
    }
    if (this.busy) {
      return false; // ignore double-taps mid-kick
    }

    this.setState({ busy: true });

    try {
      await this.kickOnce();
      this.warmUntil = Date.now() + this.WARM_MS;
      return true;
    } catch {
      // Stale/dropped handle: drop it and reconnect fresh, then try once more.
      this.kickChar = null;
      try {
        await this.kickOnce();
        this.warmUntil = Date.now() + this.WARM_MS;
        return true;
      } catch (second) {
        this.setState({ connected: false });
        this.reportConnectFailure(second);
        return false;
      }
    } finally {
      this.setState({ busy: false });
    }
  }

  /**
   * Connects (pairing first if needed) without kicking. Drives the Connect button, so
   * it runs inside a click and is allowed to open the browser's device chooser.
   */
  async connect(): Promise<boolean> {
    if (!this.supported) {
      this.report('error', 'Drawer Unavailable', 'This browser cannot talk to the cash drawer. Use Chrome or Edge over HTTPS.');
      return false;
    }
    if (this.busy) {
      return false;
    }

    this.setState({ busy: true });

    try {
      //Two things matter here, and this method previously had neither, which is why it
      //failed where No Sale succeeded.
      //
      //Nothing may be awaited before this call: the browser only allows the device chooser
      //to open while the click that triggered it is still being handled.
      //
      //And the first attempt has to be allowed to fail. Pairing and the initial GATT
      //connect are flaky - openDrawer() has always quietly retried once and that retry is
      //what usually lands. Without it the first stumble was reported as a hard failure.
      await this.ensureReady();
      return true;
    } catch {
      this.kickChar = null;

      try {
        await this.ensureReady();
        return true;
      } catch (second) {
        this.setState({ connected: false });
        this.reportConnectFailure(second);
        return false;
      }
    } finally {
      this.setState({ busy: false });
    }
  }

  /**
   * Kick used at the end of a sale. That runs after a payment response rather than inside
   * a click, so the chooser cannot be opened - but re-connecting to a drawer the browser
   * has already been given permission for does not need a gesture, which is what the
   * restore below recovers.
   */
  async openDrawerIfPaired(): Promise<boolean> {
    if (!this.paired) {
      await this.restoreConnection();
    }

    if (!this.paired) {
      this.report('warn', 'Drawer Not Connected', 'The drawer was not opened. Use Connect on the till toolbar to pair it.');
      return false;
    }

    return await this.openDrawer();
  }

  /**
   * Recovers a drawer the browser has already been permitted to use, so the connection
   * survives a page reload without anyone having to press Connect again. Chrome only, and
   * silently does nothing where getDevices() is unavailable.
   */
  async restoreConnection(): Promise<void> {
    if (this.device) {
      return;
    }

    const bluetooth = (navigator as any).bluetooth;

    if (!bluetooth || !bluetooth.getDevices) {
      return;
    }

    try {
      const devices = await bluetooth.getDevices();
      const found = (devices ?? []).find((d: any) => (d.name ?? '').indexOf('CashDrawer') === 0);

      if (found) {
        this.device = found;
        this.setState({ deviceName: found.name ?? '(unnamed device)' });
        found.addEventListener('gattserverdisconnected', () => this.onDisconnect());
      }
    } catch {
      // No permitted devices, or the browser refused - Connect will pair from scratch.
    }
  }

  private async kickOnce(): Promise<void> {
    const char = await this.ensureReady();
    // Prefer write-with-response: a successful resolve means the device actually
    // ACKed the bytes, so it is real confirmation of delivery.
    if (char.properties.write) {
      await char.writeValue(KICK_PIN2);
    } else {
      await char.writeValueWithoutResponse(KICK_PIN2);
    }
  }

  /** Returns a live, cached write-characteristic, connecting only if needed. */
  private async ensureReady(): Promise<any> {
    // Fast path: already connected and handle cached, so no BLE round-trips.
    if (this.device?.gatt?.connected && this.kickChar) {
      return this.kickChar;
    }

    // First ever use needs the chooser.
    if (!this.device) {
      this.device = await (navigator as any).bluetooth.requestDevice({
        filters: [{ namePrefix: 'CashDrawer' }],
        optionalServices: [DRAWER_SERVICE]
      });
      this.setState({ deviceName: this.device.name ?? '(unnamed device)' });
      this.device.addEventListener('gattserverdisconnected', () => this.onDisconnect());
    }

    await this.connectAndCache();
    return this.kickChar;
  }

  /** Connect, grab the vendor service and write characteristic, cache the handle. */
  private async connectAndCache(): Promise<void> {
    let lastErr: any;

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const server = await this.device.gatt.connect();
        // Brief settle - hitting service discovery instantly is the main cause of
        // the "immediate drop" seen on Windows.
        await this.delay(250);

        if (!this.device.gatt.connected) {
          throw new Error('link dropped right after connecting');
        }

        const service = await server.getPrimaryService(DRAWER_SERVICE);
        this.kickChar = await service.getCharacteristic(DRAWER_WRITE_CHAR);
        this.setState({ connected: true });
        //Open the warm window as soon as we are connected, not just after a successful
        //kick. A drawer that drops the link seconds after connecting would otherwise fall
        //outside the window and never re-warm, leaving it showing as disconnected.
        this.warmUntil = Date.now() + this.WARM_MS;
        return;
      } catch (err) {
        lastErr = err;
        await this.delay(400);
      }
    }

    throw lastErr;
  }

  private onDisconnect(): void {
    this.setState({ connected: false });
    this.kickChar = null;

    // Re-warm in the background so the next press is instant, but only within the
    // warm window so an idle drawer eventually stops reconnecting.
    if (this.keepWarm && Date.now() < this.warmUntil) {
      void this.rewarm();
    }
  }

  private async rewarm(): Promise<void> {
    if (this.reconnecting || !this.device) {
      return;
    }

    this.reconnecting = true;

    try {
      await this.delay(300); // let the stack settle after the drop

      if (Date.now() >= this.warmUntil) {
        return; // window closed while waiting
      }

      await this.connectAndCache();
    } catch {
      // Silent: the next openDrawer() will reconnect on demand.
    } finally {
      this.reconnecting = false;
    }
  }

  /** Manual full disconnect (also stops keep-warm). */
  disconnect(): void {
    this.warmUntil = 0;
    this.device?.gatt?.disconnect();
    this.setState({ connected: false });
    this.kickChar = null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }
}
